import { lazy, Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider
} from "react-router-dom";
import "./App.css";
import { AdminRoute } from "./components/AdminRoute";
import { LawFirmSettings } from "./components/LawFirmSettings";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RouteErrorPage } from "./components/common/RouteErrorPage";
import {
  PORTAL_PATH,
  PORTAL_REQUEST_PATH,
  PORTAL_SIGN_IN_PATH,
  PORTAL_SPEND_PATH,
  PortalEmployeesPage,
  PortalRequestPage,
  PortalShell,
  PortalSignInPage,
  PortalSpendPage
} from "./features/companyPortal";
import {
  CLIENT_PORTAL_CASE_PATH,
  CLIENT_PORTAL_DOCUMENTS_PATH,
  CLIENT_PORTAL_PATH,
  CLIENT_PORTAL_PROFILE_PATH,
  CLIENT_PORTAL_SIGN_IN_PATH,
  CLIENT_PORTAL_SIGN_PATH,
  CLIENT_PORTAL_TASKS_PATH,
  CLIENT_PORTAL_WELCOME_PATH,
  ClientPortalShell,
  ClientPortalSignInPage,
  LegacyClientPortalRedirect,
  PortalCasePage,
  PortalCasesPage,
  PortalDocumentsPage,
  PortalHomePage,
  PortalProfilePage,
  PortalSignPage,
  PortalTasksPage,
  PortalWelcomePage
} from "./features/clientPortal";
import { RecaptchaContainer } from "./components/RecaptchaContainer";
import { firmConfiguration } from "./constans";
import { FirmCalendarPage } from "./features/calendar/pages/FirmCalendarPage";
import { BillingReviewPage } from "./features/timeTracking/pages/BillingReviewPage";
import { TimesheetPage } from "./features/timeTracking/pages/TimesheetPage";
import { TIME_PATH, TIME_REVIEW_PATH } from "./features/timeTracking/routes";
import { CALENDAR_PATH } from "./features/calendar/routes";
import { HomePage } from "./features/dashboard/pages/HomePage";
import { HOME_PATH } from "./features/dashboard/routes";
import { ONBOARDING_PATH } from "./features/onboarding/routes";
import { OnboardingPage } from "./features/onboarding/pages/OnboardingPage";
import { CaseWorkspacePage } from "./features/caseIde";
import {
  CaseIndexRedirect,
  CaseWorkspaceRedirect
} from "./features/caseIde/CaseWorkspaceRedirect";
import { LegacyCaseRedirect } from "./features/caseIde/LegacyCaseRedirect";
import { CASE_HOME_SEGMENT, NEW_CASE_PATH } from "./features/caseIde/routes";
import { NewCaseRedirect } from "./features/newCase";
import { ConnectorsPage } from "./features/connectors/pages/ConnectorsPage";
import { CONNECTORS_PATH } from "./features/connectors/routes";
import TutorialsPage from "./features/tutorials/pages/TutorialsPage";
import { TUTORIALS_PATH } from "./features/tutorials/routes";
import { CompaniesPage } from "./features/companies/pages/CompaniesPage";
import { CompanyPage } from "./features/companies/pages/CompanyPage";
import { MigrationReviewPage } from "./features/companies/pages/MigrationReviewPage";
import {
  COMPANIES_PATH,
  COMPANIES_REVIEW_PATH
} from "./features/companies/routes";
import { AutomationsPage } from "./features/automations/pages/AutomationsPage";
import { AUTOMATIONS_PATH } from "./features/automations/routes";
import { FirmKnowledgePage } from "./features/firmKnowledge/pages/FirmKnowledgePage";
import { StudioRedirect } from "./features/firmKnowledge/StudioRedirect";
import { FIRM_KNOWLEDGE_PATH } from "./features/firmKnowledge/routes";
import { ResearchPage } from "./features/research/pages/ResearchPage";
import { RESEARCH_PATH } from "./features/research/routes";
import { setFavicon } from "./helpers/dom_helpers";
import { getSubdomain } from "./helpers/helpers";
import { useEntitlements } from "./hooks/useEntitlements";
import { Feature as PlanFeature } from "./types/entitlements";
import AcceptAgreement from "./pages/AcceptAgreement";
import { Account } from "./pages/Account";
import { AdminCustomers } from "./pages/Admin/Customers/AdminCustomers";
import { DefaultTemplatesRoutes } from "./pages/Admin/DefaultTemplates";
import { DocumentsChecklist } from "./pages/Admin/DocumentsChecklist";
import { AdminExhibitListFormats } from "./pages/Admin/ExhibitListFormats/AdminExhibitListFormat";

import { LawFirmsRoutes } from "./pages/Admin/LawFirms";
import { AdminUsageMonitoringNotifications } from "./pages/Admin/Monitors/UsageMonitors";
import AdminUpdatesManager from "./pages/Admin/Updates/AdminUpdatesManager";
import AdminEditUser from "./pages/Admin/Users/AdminEditUser";
import { AdminUsers } from "./pages/Admin/Users/AdminUsers";
import { CasesCmsSwitch } from "./pages/CasesCmsSwitch";
import IndividualCase from "./pages/IndividualCase";
import { IndividualProfile } from "./pages/IndividualProfile";
import { Login } from "./features/login";
import { NewCase } from "./pages/NewCase";
import NotFound from "./pages/NotFound";
import { PricingPage } from "./pages/PricingPage";
import { RedirectHandler } from "./pages/RedirectHandler";
import { ResetPassword } from "./pages/ResetPassword";
import { AUTH_ACTION_PATH, AuthActionPage } from "./features/authAction";
import { LawFirmSsoSetup } from "./pages/LawFirmSsoSetup";
import { LawFirmSsoManagement } from "./pages/LawFirmSsoManagement";
import { ForeignNationalsRoutes } from "./pages/ForeignNationals";
import { PublicOutgoingIntake } from "./pages/PublicOutgoingIntake/PublicOutgoingIntake";
import { SupportScripts } from "./pages/SupportScripts";
import { VisaList } from "./pages/VisaList";
import { VisaQuestionnaireBuilder } from "./pages/VisaQuestionnaireBuilder";
import { PlaygroundPage } from "./features/playground/pages/PlaygroundPage";
import { UserProfilesProvider } from "./features/userProfiles/context/UserProfilesContext";
import { DESIGN_SYSTEM_PATH } from "./features/designSystem/routes";
import { reloadsOnNavigation, useAppUpdateStore } from "./features/appUpdate";

const DesignSystemPage = lazy(() =>
  import("./features/designSystem/pages/DesignSystemPage").then((m) => ({
    default: m.DesignSystemPage
  }))
);

function App() {
  const subdomain = getSubdomain();
  const { hasFeature } = useEntitlements();
  const isAthenaInPlan = hasFeature(PlanFeature.ATHENA);

  // Updates the document title and favicon based on the current subdomain.
  // Defaults to "CaseBlink" if no matching title or favicon is found.
  useEffect(() => {
    window.document.title =
      firmConfiguration[subdomain]?.tabTitle ?? "CaseBlink";
    const faviconUrl = firmConfiguration[subdomain]?.favicon;
    if (faviconUrl) setFavicon(faviconUrl);
  }, []);

  // Pages under a case, shared by the client-scoped and the bare case roots.
  // The workspace is the only page; every retired case-prep URL redirects in.
  const caseRoutes = (
    <>
      <Route index element={<CaseIndexRedirect />} />
      <Route
        path={CASE_HOME_SEGMENT}
        element={<CaseWorkspacePage />}
        errorElement={<RouteErrorPage />}
      />
      <Route path="*" element={<CaseWorkspaceRedirect />} />
    </>
  );

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/" element={<RedirectHandler />} />
        <Route path="login" element={<Login />} />
        <Route path="intakes/:token" element={<PublicOutgoingIntake />} />
        <Route path="accept-agreement" element={<AcceptAgreement />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path={AUTH_ACTION_PATH} element={<AuthActionPage />} />
        <Route
          path={CLIENT_PORTAL_SIGN_IN_PATH}
          element={<ClientPortalSignInPage />}
        />
        <Route
          path={CLIENT_PORTAL_WELCOME_PATH}
          element={<PortalWelcomePage />}
        />
        <Route element={<ClientPortalShell />}>
          <Route path={CLIENT_PORTAL_PATH} element={<PortalHomePage />} />
          <Route path={CLIENT_PORTAL_CASE_PATH} element={<PortalCasesPage />} />
          <Route
            path={`${CLIENT_PORTAL_CASE_PATH}/:caseId`}
            element={<PortalCasePage />}
          />
          <Route
            path={CLIENT_PORTAL_TASKS_PATH}
            element={<PortalTasksPage />}
          />
          <Route
            path={CLIENT_PORTAL_DOCUMENTS_PATH}
            element={<PortalDocumentsPage />}
          />
          <Route
            path={CLIENT_PORTAL_PROFILE_PATH}
            element={<PortalProfilePage />}
          />
          <Route
            path={`${CLIENT_PORTAL_SIGN_PATH}/:requestId`}
            element={<PortalSignPage />}
          />
        </Route>
        <Route path="/client" element={<LegacyClientPortalRedirect />} />
        <Route path={PORTAL_SIGN_IN_PATH} element={<PortalSignInPage />} />
        <Route element={<PortalShell />}>
          <Route path={PORTAL_PATH} element={<PortalEmployeesPage />} />
          <Route path={PORTAL_REQUEST_PATH} element={<PortalRequestPage />} />
          <Route path={PORTAL_SPEND_PATH} element={<PortalSpendPage />} />
        </Route>
        <Route element={<ProtectedRoute />}>
          <Route path="/law-firm/sso-setup" element={<LawFirmSsoSetup />} />
          <Route
            path="/law-firm/sso-management"
            element={<LawFirmSsoManagement />}
          />
          {/*
            A case lives under its client: /clients/:fnId/cases/:individualId.
            Cases with no client link use /cases/:individualId; the shell moves
            them onto the client-scoped URL once the matter resolves.
          */}
          <Route
            path="/clients/:foreignNationalId/cases/:id"
            element={<IndividualCase />}
          >
            {caseRoutes}
          </Route>
          <Route path="/cases/:id" element={<IndividualCase />}>
            {caseRoutes}
          </Route>

          {/* Retired scheme: /individual/:id/:visaType/... (account stays). */}
          <Route path="/individual/:id/account" element={<Account />}>
            <Route path="profile" element={<IndividualProfile />} />
          </Route>
          <Route path="/individual/:id" element={<LegacyCaseRedirect />} />
          <Route
            path="/individual/:id/:visaType/*"
            element={<LegacyCaseRedirect />}
          />
          <Route element={<AdminRoute />}>
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/users/:uid/edit" element={<AdminEditUser />} />
            <Route path="/admin/updates" element={<AdminUpdatesManager />} />
            <Route path="/admin/visa-settings" element={<VisaList />} />
            <Route
              path="/admin/default-templates/*"
              element={<DefaultTemplatesRoutes />}
            />
            <Route path="/admin/support-scripts" element={<SupportScripts />} />
            <Route
              path="/admin/documents-checklist"
              element={<DocumentsChecklist />}
            />
            <Route
              path="/admin/monitors"
              element={<AdminUsageMonitoringNotifications />}
            />
            <Route
              path="/admin/visa-questionnaire-builder/:visa"
              element={<VisaQuestionnaireBuilder />}
            />
            <Route path="/admin/customers" element={<AdminCustomers />} />
            <Route
              path={DESIGN_SYSTEM_PATH}
              element={
                <Suspense fallback={null}>
                  <DesignSystemPage />
                </Suspense>
              }
            />
            <Route path="/admin/law-firms/*" element={<LawFirmsRoutes />} />
          </Route>
          <Route
            path="/admin/exhibit-list-formats"
            element={<AdminExhibitListFormats />}
          />
          <Route path="/individual/lawfirm" element={<LawFirmSettings />} />

          <Route path="/cases" element={<CasesCmsSwitch />} />
          <Route path={NEW_CASE_PATH} element={<NewCase />} />
          {/* The lawyer landing page: the practice dashboard. */}
          <Route path={HOME_PATH} element={<HomePage />} />
          {/* The guided first run: one conversation, no sidebar. */}
          <Route path={ONBOARDING_PATH} element={<OnboardingPage />} />
          {/* The firm calendar: every client's reminders plus synced calendars. */}
          <Route path={CALENDAR_PATH} element={<FirmCalendarPage />} />
          {/* Your own timesheet, and Billing review for firm admins. */}
          <Route path={TIME_PATH} element={<TimesheetPage />} />
          <Route path={TIME_REVIEW_PATH} element={<BillingReviewPage />} />
          {/* The dashboard's URL before Home replaced it. */}
          <Route
            path="/dashboard"
            element={<Navigate to={HOME_PATH} replace />}
          />
          {/* Corporate clients: the employers whose cases the firm handles. */}
          <Route path={COMPANIES_PATH} element={<CompaniesPage />} />
          <Route
            path={COMPANIES_REVIEW_PATH}
            element={<MigrationReviewPage />}
          />
          <Route
            path={`${COMPANIES_PATH}/:companyId`}
            element={<CompanyPage />}
          />
          <Route path="/clients/*" element={<ForeignNationalsRoutes />} />
          <Route
            path="/athena"
            element={
              isAthenaInPlan ? (
                <PlaygroundPage />
              ) : (
                <Navigate to="/cases" replace />
              )
            }
          />
          <Route
            path="/playground"
            element={<Navigate to="/athena" replace />}
          />
          {/* The firm's library of exemplar documents and its standing instructions. */}
          <Route path={FIRM_KNOWLEDGE_PATH} element={<FirmKnowledgePage />} />
          {/* The CaseBlink immigration library: decisions, statutes, manuals, policy. */}
          <Route path={RESEARCH_PATH} element={<ResearchPage />} />
          <Route path={AUTOMATIONS_PATH} element={<AutomationsPage />} />
          {/* The attorney's connected apps (Gmail, Calendar, ...) their agents use. */}
          <Route path={CONNECTORS_PATH} element={<ConnectorsPage />} />
          {/* Tutorial videos, newest first; a new one also pops up once. */}
          <Route
            path={`${TUTORIALS_PATH}/:tutorialId?`}
            element={<TutorialsPage />}
          />
          {/* Retired Studio; old bookmarks land in Firm Knowledge. */}
          <Route path="/studio/*" element={<StudioRedirect />} />
          {/* Retired page; keep old bookmarks landing on Cases. */}
          <Route
            path="/archived-cases"
            element={<Navigate to="/cases" replace />}
          />
          {/* Retired path; old bookmarks land on the wizard. */}
          <Route path="/new-case" element={<NewCaseRedirect />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </>
    )
  );

  // A waiting build loads on the next change of page, at the new page.
  useEffect(() => {
    let path = router.state.location.pathname;
    return router.subscribe((state) => {
      const next = state.location.pathname;
      const reload =
        state.navigation.state === "idle" &&
        reloadsOnNavigation(useAppUpdateStore.getState().update, path, next);
      path = next;
      if (reload) window.location.reload();
    });
  }, [router]);

  return (
    <div className="App">
      <RecaptchaContainer />
      <UserProfilesProvider>
        <RouterProvider router={router} />
      </UserProfilesProvider>
    </div>
  );
}

export default App;
